import { request } from '@umijs/max';

const CURRENT_USER_INFO_CACHE_TTL = 5000;
let currentUserInfoPromise: Promise<any> | null = null;
let currentUserInfoCache: any = null;
let currentUserInfoCacheAt = 0;

export async function fetchUserPermissions(appUuid?: string) {
  return request('/api/sys-user/permissions', {
    method: 'POST',
    data: {
      appUuid: appUuid || '',
    },
  });
}

export async function recordAppSwitchLog(appUuid: string) {
  return request('/api/sys-user/app-switch-log', {
    method: 'POST',
    data: {
      uuid: appUuid || '',
    },
  });
}

export async function fetchAvailableApps(userUuid?: string) {
  const res = await request('/api/application/items', {
    method: 'POST',
    data: {
      page: 1,
      size: 1000,
      status: 1,
      userUuid: userUuid || undefined,
    },
  });
  const body = (res as any)?.data ?? res;
  const items = body?.items ?? body?.data?.items;
  const list = Array.isArray(items?.list)
    ? items.list
    : Array.isArray(body?.list)
      ? body.list
      : [];
  return list;
}

const uniquePermissionCodes = (permissions: unknown[]): string[] =>
  Array.from(
    new Set(
      permissions.filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0,
      ),
    ),
  );

const collectPermissionsFromObject = (payload: any): string[] => {
  if (!payload || typeof payload !== 'object') return [];
  const list = [
    ...(Array.isArray(payload.permissions) ? payload.permissions : []),
    ...(Array.isArray(payload.permissionCodes) ? payload.permissionCodes : []),
    ...(Array.isArray(payload.allPermissions) ? payload.allPermissions : []),
  ];
  return uniquePermissionCodes(list);
};

export async function fetchCurrentUserInfo(options?: {
  preferCache?: boolean;
}) {
  const { preferCache = false } = options || {};
  const now = Date.now();

  if (
    preferCache &&
    currentUserInfoCache &&
    now - currentUserInfoCacheAt < CURRENT_USER_INFO_CACHE_TTL
  ) {
    return currentUserInfoCache;
  }

  if (currentUserInfoPromise) {
    return currentUserInfoPromise;
  }

  currentUserInfoPromise = request('/api/sys/current_user_info', {
    method: 'POST',
  })
    .then((res) => {
      currentUserInfoCache = res;
      currentUserInfoCacheAt = Date.now();
      return res;
    })
    .finally(() => {
      currentUserInfoPromise = null;
    });

  return currentUserInfoPromise;
}

export const clearCurrentUserInfoCache = () => {
  currentUserInfoPromise = null;
  currentUserInfoCache = null;
  currentUserInfoCacheAt = 0;
};

export const extractCurrentUserInfo = (payload: any) => {
  if (payload?.data?.user) return payload.data.user;
  if (payload?.data?.data?.user) return payload.data.data.user;
  if (payload?.data?.data) return payload.data.data;
  if (payload?.data) return payload.data;
  return payload;
};

export const extractCurrentUserPermissions = (payload: any): string[] => {
  const userInfo = extractCurrentUserInfo(payload);
  const fromUserInfo = collectPermissionsFromObject(userInfo);
  if (fromUserInfo.length > 0) {
    return fromUserInfo;
  }

  const data = payload?.data ?? payload ?? {};
  const fromData = uniquePermissionCodes([
    ...collectPermissionsFromObject(data),
    ...collectPermissionsFromObject(data?.data),
    ...collectPermissionsFromObject(payload),
  ]);
  return fromData;
};

export const isUserDisabled = (payload: any) => {
  const userInfo = extractCurrentUserInfo(payload);
  const status = userInfo?.status;
  if (status === undefined || status === null || status === '') {
    return false;
  }
  return Number(status) === 0;
};

export function normalizePermissionCodes(payload: any, appUuid?: string): string[] {
  const directPermissions = extractCurrentUserPermissions(payload);
  if (directPermissions.length > 0) {
    return directPermissions;
  }

  if (Array.isArray(payload)) return uniquePermissionCodes(payload);
  if (Array.isArray(payload?.data)) return uniquePermissionCodes(payload.data);
  if (Array.isArray(payload?.data?.data)) return uniquePermissionCodes(payload.data.data);

  const data = payload?.data ?? payload ?? {};
  const roles = Array.isArray(data?.roles) ? data.roles : [];
  const matchedRoles = appUuid
    ? roles.filter((role: any) => !role?.appUuid || role.appUuid === appUuid)
    : roles;
  const rolePermissions = uniquePermissionCodes(
    matchedRoles.flatMap((role: any) =>
      Array.isArray(role?.permissions) ? role.permissions : [],
    ),
  );

  if (rolePermissions.length > 0) {
    return rolePermissions;
  }

  if (Array.isArray(data?.allPermissions)) {
    return uniquePermissionCodes(data.allPermissions);
  }

  return [];
}
